import React, { useState, useRef, useEffect } from 'react';
import './callHistoryModal.css';

const CallHistoryModal = ({ isVisible, onClose, calls, onViewCallDetails, onRefreshCall, isLoading, initialScrollTop }) => {
    const [refreshingCalls, setRefreshingCalls] = useState({});
    const contentRef = useRef(null);
    useEffect(() => {
        if (isVisible && contentRef.current) {
            contentRef.current.scrollTop = initialScrollTop || 0;
        }
    }, [isVisible, initialScrollTop]);

    if (!isVisible) return null;

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return '#3BB765';
            case 'in_progress':
            case 'queued':
                return '#FFB700';
            case 'failed':
                return '#F35824';
            default:
                return '#516f90';
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'Completed';
            case 'in_progress':
                return 'In Progress';
            case 'queued':
                return 'Queued';
            case 'failed':
                return 'Failed';
            default:
                return status || 'Unknown';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        const truncatedSecs = Math.floor(secs * 100) / 100;

        return `${mins}m ${truncatedSecs.toFixed(2)}s`;
    };

    const isCallRefreshable = (status) => {
        const lowerStatus = status?.toLowerCase();
        return lowerStatus !== 'completed' && lowerStatus !== 'failed';
    };

    const handleRefreshCall = async (executionId) => {
        setRefreshingCalls(prev => ({ ...prev, [executionId]: true }));
        try {
            await onRefreshCall(executionId);
        } finally {
            setRefreshingCalls(prev => ({ ...prev, [executionId]: false }));
        }
    };

    return (
        <div className="call-history-modal-overlay">
            <div className="call-history-modal-container">
                <div className="call-history-modal-header">
                    <h2>Call History</h2>
                    <button className="call-history-close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>

                {isLoading ? (
                    <div className="call-history-loading">
                        <div className="call-history-spinner"></div>
                        <p>Loading call history...</p>
                    </div>
                ) : calls && calls.length > 0 ? (
                    <div className="call-history-content" ref={contentRef}>
                        <div className="call-history-list">
                            {calls.map((call, index) => (
                                <div key={call.executionId || index} className="call-history-item">
                                    <div className="call-history-item-header">
                                        <span
                                            className="call-history-status-badge"
                                            style={{ backgroundColor: getStatusColor(call.callStatus) }}
                                        >
                                            {getStatusLabel(call.callStatus)}
                                        </span>
                                        <span className="call-history-date">{formatDate(call.dateCreated)}</span>
                                    </div>
                                    <div className="call-history-item-details">
                                        <div className="call-history-item-info">
                                            <span className="call-history-label">Phone:</span>
                                            <span className="call-history-value">{call.phoneNumber || '-'}</span>
                                        </div>
                                        <div className="call-history-item-info">
                                            <span className="call-history-label">Duration:</span>
                                            <span className="call-history-value">{formatDuration(call.callLength)}</span>
                                        </div>
                                    </div>
                                    <div className="call-history-item-actions">
                                        {isCallRefreshable(call.callStatus) && (
                                            <button
                                                className="call-history-refresh-btn"
                                                onClick={() => handleRefreshCall(call.executionId)}
                                                disabled={refreshingCalls[call.executionId]}
                                                title="Refresh call status"
                                            >
                                                {refreshingCalls[call.executionId] ? (
                                                    <span className="call-history-refresh-spinner"></span>
                                                ) : (
                                                    '🔄'
                                                )}
                                            </button>
                                        )}
                                        <button
                                            className="call-history-view-btn"
                                            onClick={() =>
                                                onViewCallDetails(
                                                    call.executionId,
                                                    contentRef.current?.scrollTop || 0
                                                )
                                            }
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="call-history-empty">
                        <p>No calls have been made for this candidate yet.</p>
                    </div>
                )}

                <div className="call-history-modal-actions">
                    <button className="call-history-close-action-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CallHistoryModal;